import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  InputAdornment,
  TextField,
  TableCell,
  TableBody,
  Checkbox
} from '@mui/material';
import { AccountInformation } from 'src/models/accountManagement';
import { withStyles } from '@mui/styles';
import { useNavigate } from 'react-router';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import SearchIcon from '@mui/icons-material/Search';
import SkillsOverview from './SkillsOverview';
import { SkillInformation, Skill } from 'src/models/Skills';
import {
  getSingleSkill,
  getSkillsList
} from 'src/services/skillManagementService';

interface Filters {
  status?: any;
}

const SkillsManagementTable = () => {
  const navigate = useNavigate();

  const applyFilters = (
    skillList: SkillInformation[],
    filters: Filters
  ): SkillInformation[] => {
    return (
      skillList?.length > 0 &&
      skillList?.filter((accountStatus) => {
        let matches = true;
        return matches;
      })
    );
  };

  const NavigateToSingleSkill = (skillName: string) => {
    navigate('single-skill/' + skillName);
  };

  const applyPagination = (
    skillList: SkillInformation[],
    page: number,
    limit: number
  ): SkillInformation[] => {
    return (
      skillList?.length > 0 &&
      skillList?.slice(page * limit, page * limit + limit)
    );
  };

  const getAllSkillList = () => {
    getSkillsList().then((res) => {
      setSkillList(res.data);
    });
  };

  useEffect(() => {
    getAllSkillList();
  }, []);

  const initalSkillList: SkillInformation[] = [];
  const [skillList, setSkillList] = useState(initalSkillList);

  const [sortIdType, setSortIdType] = useState('desc');
  const [sortNameType, setSortNameType] = useState('asc');

  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [filters, setFilters] = useState<Filters>({ status: null });
  const [filteredAccount, setfilteredAccount] = useState<any>(
    applyFilters(skillList, filters)
  );

  const [paginatedLists, setpaginatedLists] = useState<any>(
    applyPagination(skillList, page, limit)
  );

  const [searchKeywords, setSearchKeywords] = useState('');

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value));
  };

  useEffect(() => {
    if (skillList?.length > 0 && sortIdType === 'asc') {
      setSkillList(skillList.sort((a, b) => a.skill.localeCompare(b.skill)));
    } else if (skillList?.length > 0 && sortIdType === 'desc') {
      setSkillList(skillList.sort((a, b) => b.skill.localeCompare(a.skill)));
    }
  }, [skillList, sortIdType]);

  useEffect(() => {
    if (skillList?.length > 0 && sortNameType === 'asc') {
      setSkillList(skillList.sort((a, b) => a.skill.localeCompare(b.skill)));
    } else if (skillList?.length > 0 && sortNameType === 'desc') {
      setSkillList(skillList.sort((a, b) => b.skill.localeCompare(a.skill)));
    }
  }, [sortNameType]);

  useEffect(() => {
    setfilteredAccount(applyFilters(skillList, filters));
  }, [skillList, page, limit, filters, sortIdType, sortNameType]);

  useEffect(() => {
    setpaginatedLists(applyPagination(filteredAccount, page, limit));
  }, [skillList, page, limit, filteredAccount]);

  const nameChange = (e) => {
    setSearchKeywords(e.target.value);
  };

  const searchSkillsAll = async (keyword: string) => {
    const skillResult: SkillInformation[] = [];
    skillList.forEach((item) => {
      if (
        item.skill.toLocaleLowerCase().includes(keyword.toLocaleLowerCase())
      ) {
        skillResult.push(item);
      }
    });
    setSkillList(skillResult);
  };

  useEffect(() => {
    if (searchKeywords.length > 2) {
      searchSkillsAll(searchKeywords);
    }
    if (searchKeywords.length === 0) {
      getAllSkillList();
    }
  }, [searchKeywords]);

  const CustomTableRow = withStyles({
    root: {
      background: '#F5F5FA !important',
      height: '3.5rem'
    }
  })(TableRow);

  // custom typography with different text and cursor pointer
  const PrimaryTextTypography = withStyles({
    root: {
      color: '#0096FF',
      cursor: 'pointer'
    }
  })(Typography);

  return (
    <div>
      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography
            style={{
              marginLeft: '0px',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
          >
            Skills
          </Typography>
          <Typography variant="subtitle2"></Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <SkillsOverview />
        </Grid>
      </Grid>
      <Card>
        <Grid
          container
          style={{
            justifyContent: 'space-between',
            marginBottom: 7,
            padding: '1rem'
          }}
          spacing={2}
        >
          <Grid item xs={6}>
            <TextField
              sx={{ width: 150, mr: 2 }}
              size="small"
              placeholder="Search..."
              onChange={(e) => nameChange(e)}
              InputProps={{
                style: { fontSize: '12px', height: '1.75rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: '#d3d3d3' }}
                      style={{ fontSize: '15px' }}
                    />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
        <TableContainer className="tableContainer">
          <Table size="small">
            <TableHead>
              <CustomTableRow>
                <TableHeaderCell content="" smallFont />
                <TableHeaderCell
                  content="Skill"
                  smallFont
                  sort
                  setSortType={setSortIdType}
                  sortType={sortIdType}
                />
                <TableHeaderCell
                  content="Total Resources"
                  smallFont
                  sort
                  setSortType={setSortNameType}
                  sortType={sortNameType}
                />
                <TableHeaderCell content="Available Resoures" smallFont />
                <TableHeaderCell content="" smallFont />
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {paginatedLists &&
                paginatedLists?.length > 0 &&
                paginatedLists.map((listItem) => {
                  return (
                    <TableRow hover key={listItem.code}>
                      <TableCell style={{ width: '10%' }}>
                        <Checkbox color="primary" />
                      </TableCell>
                      <TableCell>
                        <PrimaryTextTypography
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          onClick={() => {
                            NavigateToSingleSkill(listItem.skill);
                          }}
                          gutterBottom
                          noWrap
                        >
                          {listItem.skill}
                        </PrimaryTextTypography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {listItem.totalCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {listItem.remainingCount}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box p={2}>
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={filteredAccount?.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
    </div>
  );
};

export default SkillsManagementTable;
